import { faLocationDot } from "@fortawesome/pro-light-svg-icons";
import { useState } from "react";
import * as yup from "yup";
import { PrimaryButton } from "../../../../ui/Buttons";
import { SearchInput } from "../../../../ui/Inputs";
import { isValidCoordinates } from "../../../../utils/geopatialUtils";
import { useMapData } from "../../hooks/mapDataAndFilters";
import { useMap } from "../../hooks/mapState";
import { ToolBarDropdown } from "../../ui/MapControlsV2";
import { MenuCategory } from "../../ui/Menu";

// Define a schema for the coordinates
const coordinatesSchema = yup.object({
    coordinates: yup
        .string()
        .required("Coordinates are required")
        .matches(
            /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/,
            "Coordinates must be in the format latitude,longitude.",
        )
        .test(
            "isValidCoordinates",
            "Latitude must be between -90 and 90, and Longitude must be between -180 and 180.",
            (value) => {
                const [lat, lon] = value
                    .split(",")
                    .map((num) => parseFloat(num.trim()));
                return isValidCoordinates(lat, lon);
            },
        ),
});

interface GoToLocationControlProps {
    className?: string;
}

export const GoToLocationControl = (props: GoToLocationControlProps) => {
    const { flyTo } = useMap("mainMap");
    const [coordinates, setCoordinates] = useState("");
    const [error, setError] = useState("");
    const { setSelectedContext, selectedContext } = useMapData("mainMap");

    const navigateToCoords = async () => {
        try {
            // Validate coordinates
            await coordinatesSchema.validate({ coordinates });
            const split = coordinates.split(",");
            const lat = parseFloat(split[0]);
            const lon = parseFloat(split[1]);
            if (Number.isNaN(lat) || Number.isNaN(lon)) {
                throw new Error("Invalid numbers for coordinates.");
            }
            flyTo(lat, lon, 18);
            setSelectedContext({
                ...selectedContext,
                selectedLocation: {
                    type: "Point",
                    coordinates: [lon, lat],
                },
            });
            setError(""); // Clear error if everything is fine
            setCoordinates("");
        } catch (err) {
            setError(err.message); // Set error message from yup or parsing error
        }
    };

    return (
        <ToolBarDropdown
            enabled={true}
            icon={faLocationDot}
            label="Go to"
            className={props.className}
        >
            <div className="w-64">
                <MenuCategory>Go to coordinates (EPSG 4326):</MenuCategory>
                <div className="flex flex-row space-x-2 mt-1">
                    <SearchInput
                        onKeyUp={(e) => e.key === "Enter" && navigateToCoords()}
                        value={coordinates}
                        onChange={(value) => setCoordinates(value)}
                        placeholder="31.750, -103.193"
                        className="w-full"
                    />
                    <PrimaryButton onClick={navigateToCoords}>Go</PrimaryButton>
                </div>
                {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
            </div>
        </ToolBarDropdown>
    );
};
